import { Box, Dialog, Stack } from '@mui/material';
import SuiTypography from '@upheal/design-system/components/legacy/Sui/SuiTypography';

import { ImageCircle } from '@upheal/shared/components/shared/ImageCircle';
import { mdiFileSign } from '@mdi/js';
import MdiIcon from '@upheal/design-system/components/legacy/MdiIcon';
import { useConsentConfirmation } from './useConsentConfirmation';
import SuiLoadingButton from '@upheal/design-system/components/legacy/Sui/SuiLoadingButton';
import { useCallback } from 'react';
import {
    uiEventDataAttrs,
    trackUiEvent,
} from '../../../utils/analytics/ui-tracking';
import { useMount } from 'react-use';

export const ConsentNewUserContent = ({
    isCentered = false,
}: {
    isCentered?: boolean;
}) => {
    return (
        <Stack spacing={5}>
            <Stack spacing={1}>
                <SuiTypography
                    color="dark"
                    variant="h4"
                    fontWeight="bold"
                    textAlign="center"
                >
                    Consent for data processing
                </SuiTypography>
                <SuiTypography
                    variant="body1"
                    color="dark"
                    textAlign={isCentered ? 'center' : 'inherit'}
                >
                    Before using Upheal with a client, be sure to obtain your{' '}
                    <span style={{ fontWeight: 'bold' }}>client's consent</span>{' '}
                    to process their data. While Upheal provides tools and
                    templates to help with this process, the responsibility to
                    collect and manage consent lies with you as the provider.
                </SuiTypography>
            </Stack>
        </Stack>
    );
};

export const ConsentOldUserContent = () => {
    return (
        <Stack spacing={5}>
            <Stack spacing={1}>
                <SuiTypography
                    color="dark"
                    variant="h4"
                    fontWeight="bold"
                    textAlign="center"
                >
                    Changes to consent collection
                </SuiTypography>
                <Stack spacing={1}>
                    <SuiTypography variant="body1" color="dark">
                        Based on your feedback, we’ve updated our consent
                        collection process.
                    </SuiTypography>

                    <SuiTypography variant="body1" color="dark">
                        Our consent documents are now shorter and simpler, and
                        we’ll{' '}
                        <span style={{ fontWeight: 'bold' }}>
                            no longer require
                        </span>{' '}
                        you to confirm consent collection for each client before
                        using Upheal. However, please remember that obtaining
                        consent remains your responsibility.
                    </SuiTypography>
                </Stack>
            </Stack>
            <Stack spacing={1}>
                <SuiTypography variant="body1" color="dark" fontWeight="bold">
                    How to get client consent:
                </SuiTypography>
                <SuiTypography variant="body1" color="dark">
                    We can assist you with emails and templates, or you can
                    handle consent on your own outside of Upheal. Explore
                    collection options in the{' '}
                    <span style={{ fontWeight: 'bold' }}>Consent</span> section
                    of any client’s profile, or in your{' '}
                    <span style={{ fontWeight: 'bold' }}>Settings</span>.
                </SuiTypography>
            </Stack>
        </Stack>
    );
};

export const ConsentConfirmationContent = () => {
    const { confirmConsent, isNewUser, isProcessingConfirmation } =
        useConsentConfirmation();

    const handleConfirmConsent = useCallback(async () => {
        try {
            await confirmConsent();
        } catch (error) {
            // error is handled in confirmConsent
        }
    }, [confirmConsent]);

    useMount(() => trackUiEvent('View', 'Consent Confirmation', 'Webapp'));

    return (
        <Box
            p={4}
            pt={6}
            sx={{
                backgroundColor: (theme) => theme.palette.offWhite.main,
            }}
        >
            <Stack spacing={4} alignItems="center">
                <ImageCircle size="large">
                    <MdiIcon
                        path={mdiFileSign}
                        color="action"
                        style={{
                            width: 48,
                            height: 48,
                        }}
                    />
                </ImageCircle>

                {isNewUser ? (
                    <ConsentNewUserContent />
                ) : (
                    <ConsentOldUserContent />
                )}

                <SuiLoadingButton
                    onClick={handleConfirmConsent}
                    variant="contained"
                    loading={isProcessingConfirmation}
                    color="action"
                    sx={{
                        width: 'fit-content',
                    }}
                    {...uiEventDataAttrs(
                        'Consent Confirmation Accepted',
                        'Webapp',
                    )}
                >
                    Ok, I understand
                </SuiLoadingButton>
            </Stack>
        </Box>
    );
};

export const ConsentConfirmation = () => {
    const {
        isConsentConfirmationOpened,
        setIsConsentConfirmationOpened,
        shouldKeepOpen,
    } = useConsentConfirmation();

    return (
        <Dialog
            open={isConsentConfirmationOpened}
            fullWidth
            onClose={() => {
                if (shouldKeepOpen) {
                    return;
                }

                setIsConsentConfirmationOpened(false);
            }}
        >
            <ConsentConfirmationContent />
        </Dialog>
    );
};
